import useRegisterDiagnosticChecklist from "routes/home/my-courses/common/diagnostics/useRegisterDiagnosticChecklist.ts";
import { DiagnosticChecklist } from "routes/home/my-courses/common/diagnostics/diagnosticChecklistTypes.ts";
import { TermMarketPrice } from "common/api/decimal/methods/course.ts";

export type MarketPriceLineItem = TermMarketPrice["lineItems"][number];

interface PvsPricingDiagnostics {
  showPricing: boolean;
  showPriceDrawer: boolean;
  showPVSActualMarketPrice: boolean;
  hasRequiredPricing: boolean;
  eaPrice: number | null;
  iaPrice: number | null;
  requiredMaterialCount: number;
  identifierCount: number;
  shouldFetch: boolean;
  npPrice: number | null;
  npWaiting: boolean;
  npError: boolean;
  npFailedLineItems: MarketPriceLineItem[];
}

function buildFailedDetail(items: MarketPriceLineItem[]): string[] {
  return items.map(
    (item) => `${item.identifier_group.join(", ")}: ${item.message}`
  );
}

function buildChecklist({
  showPricing,
  showPriceDrawer,
  showPVSActualMarketPrice,
  hasRequiredPricing,
  eaPrice,
  iaPrice,
  requiredMaterialCount,
  identifierCount,
  shouldFetch,
  npPrice,
  npWaiting,
  npError,
  npFailedLineItems,
}: PvsPricingDiagnostics): DiagnosticChecklist {
  return {
    id: "pvs-pricing",
    title: "PVS Pricing",
    items: [
      {
        label: "PVS pricing is enabled for this brand",
        status: showPricing || showPriceDrawer ? "pass" : "fail",
        detail: [
          `showPricing: ${showPricing}`,
          `showPriceDrawer: ${showPriceDrawer}`,
        ],
      },
      {
        label: "Brand shows an actual market price number",
        status: showPVSActualMarketPrice ? "pass" : "fail",
        detail: [`showActualMarketPrice: ${showPVSActualMarketPrice}`],
      },
      {
        label: "Required program pricing is available (EA/IA)",
        status: hasRequiredPricing ? "pass" : "fail",
        detail: [
          `eaPrice: ${eaPrice ?? "null"}`,
          `iaPrice: ${iaPrice ?? "null"}`,
        ],
      },
      {
        label: "Term has required materials",
        status: requiredMaterialCount > 0 ? "pass" : "fail",
        detail: [`count: ${requiredMaterialCount}`],
      },
      {
        label: "Every required material has an ISBN/VBID",
        status:
          requiredMaterialCount > 0 && identifierCount === requiredMaterialCount
            ? "pass"
            : "fail",
        detail: [
          `with identifier: ${identifierCount} of ${requiredMaterialCount}`,
        ],
      },
      {
        label: "Market price lookup was triggered",
        status: shouldFetch ? "pass" : "fail",
      },
      {
        label: "Market price lookup returned a price",
        // A request in progress is not a pass and not a failure. The item
        // above shows a request that did not start.
        status:
          !shouldFetch || npWaiting
            ? "info"
            : npPrice != null
            ? "pass"
            : "fail",
        detail: !shouldFetch
          ? ["not attempted"]
          : npWaiting
          ? ["loading…"]
          : npError
          ? ["request failed"]
          : npPrice != null
          ? [`price: ${npPrice}`]
          : npFailedLineItems.length > 0
          ? buildFailedDetail(npFailedLineItems)
          : ["price: null"],
      },
    ],
  };
}

// Receives already-computed values instead of reading its own data.
// shouldFetch, hasRequiredPricing, npPrice, npWaiting, and npError are
// derived values and local state inside useProgramPricing, and are not
// reachable from outside it.
export default function usePvsPricingDiagnostics(
  diagnostics: PvsPricingDiagnostics
) {
  useRegisterDiagnosticChecklist(buildChecklist(diagnostics));
}
